import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Mail, User, Send, CheckCircle, AlertCircle } from 'lucide-react';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';

const GuideNotification = () => {
  const { t } = useTranslation();
  const { events: gaEvents } = useGoogleAnalytics();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en'); // Default to English
  const [guide, setGuide] = useState('zendesk'); // Default to Zendesk
  const [status, setStatus] = useState('idle'); // idle, loading, success, error

  const handleSendGuide = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch(process.env.REACT_APP_BACKEND_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY
        },
        body: JSON.stringify({ email, name, language, guide }),
      });

      if (response.ok) {
        setStatus('success');
        // Track successful download request
        gaEvents.downloadGuide(email);
        
        // Close modal after 3 seconds on success
        setTimeout(() => {
          setIsModalOpen(false);
          // Reset status after closing
          setTimeout(() => {
            setStatus('idle');
            setEmail('');
            setName('');
            setLanguage('en');
            setGuide('zendesk');
          }, 500);
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Backend Error:', errorData.error || 'Unknown error');
        setStatus('error');
      }
    } catch (error) {
      console.error('Network Error:', error);
      setStatus('error');
    }
  };

  // Set initial language based on current i18n language
  useEffect(() => {
    if (isModalOpen) {
      const currentLang = localStorage.getItem('i18nextLng') || 'en';
      if (currentLang.startsWith('es')) {
        setLanguage('es');
      } else {
        setLanguage('en');
      }
    }
  }, [isModalOpen]);

  return (
    <>
      {/* Banner - Placed at the very top */}
      <div className="bg-accent/10 border-b border-accent/20 text-white py-3 px-4 text-center text-sm sm:text-base font-normal relative z-[10] w-full">
        <div className="max-w-7xl mx-auto flex items-center justify-center flex-wrap gap-x-3 gap-y-1">
          <span>
            {t('guide_notification.banner')}
          </span>
          <button 
            onClick={() => {
              setIsModalOpen(true);
              gaEvents.openGuideModal();
            }}
            className="underline font-semibold text-accent-400 hover:text-white transition-colors decoration-2 underline-offset-2"
          >
            {t('guide_notification.cta')}
          </button>
        </div>
      </div>

      {/* Modal Overlay */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 bg-[#0f2645]/40 backdrop-blur-sm animate-fade-in">
          {/* Modal Container */}
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-zoom-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Section */}
            <div className="bg-[#0f2645] p-8 text-white relative overflow-hidden">
               {/* Decorative background element */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#ffa100] rounded-full opacity-10 blur-2xl"></div>
              
              <button 
                onClick={() => setIsModalOpen(false)}
                className="absolute top-5 right-5 text-white/60 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-all duration-200"
              >
                <X className="w-5 h-5" />
              </button>
              
              <h3 className="text-2xl font-bold mb-2 leading-tight">{t('guide_notification.modal.title')}</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                {t('guide_notification.modal.subtitle')}
              </p>
            </div>

            {/* Body Section */}
            <div className="p-8 space-y-6">
               {status === 'success' ? (
                 <div className="py-8 text-center animate-fade-in">
                   <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                     <CheckCircle className="w-10 h-10" />
                   </div>
                   <h4 className="text-xl font-bold text-[#0f2645] mb-2">
                     {t('guide_notification.modal.success_message')}
                   </h4>
                 </div>
               ) : (
                 <form onSubmit={handleSendGuide} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-[#0f2645] uppercase tracking-wider mb-2 ml-1">
                      {t('guide_notification.modal.name_label')}
                    </label>
                    <div className="relative group">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-[#ffa100] transition-colors" />
                      <input
                        type="text"
                        required
                        placeholder={t('guide_notification.modal.name_placeholder')}
                        className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-[#ffa100]/10 focus:border-[#ffa100] outline-none transition-all duration-200 text-[#0f2645] font-medium"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={status === 'loading'}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#0f2645] uppercase tracking-wider mb-2 ml-1">
                      {t('guide_notification.modal.email_label')}
                    </label>
                    <div className="relative group">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-[#ffa100] transition-colors" />
                      <input
                        type="email"
                        required
                        placeholder={t('guide_notification.modal.email_placeholder')}
                        className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-[#ffa100]/10 focus:border-[#ffa100] outline-none transition-all duration-200 text-[#0f2645] font-medium"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={status === 'loading'}
                      />
                    </div>
                  </div>

                  {/* Language Selection */}
                  <div>
                    <label className="block text-xs font-bold text-[#0f2645] uppercase tracking-wider mb-3 ml-1">
                      {t('guide_notification.modal.language_label')}
                    </label>
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => setLanguage('en')}
                        className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all duration-200 font-bold text-sm ${
                          language === 'en'
                            ? 'border-[#ffa100] bg-[#ffa100]/5 text-[#ffa100]'
                            : 'border-gray-200 text-gray-400 hover:border-gray-300'
                        }`}
                        disabled={status === 'loading'}
                      >
                        {t('guide_notification.modal.lang_en')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setLanguage('es')}
                        className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all duration-200 font-bold text-sm ${
                          language === 'es'
                            ? 'border-[#ffa100] bg-[#ffa100]/5 text-[#ffa100]'
                            : 'border-gray-200 text-gray-400 hover:border-gray-300'
                        }`}
                        disabled={status === 'loading'}
                      >
                        {t('guide_notification.modal.lang_es')}
                      </button>
                    </div>
                  </div>

                  {/* Guide Selection */}
                  <div>
                    <label className="block text-xs font-bold text-[#0f2645] uppercase tracking-wider mb-3 ml-1">
                      {t('guide_notification.modal.guide_label')}
                    </label>
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => setGuide('zendesk')}
                        className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all duration-200 font-bold text-sm ${
                          guide === 'zendesk'
                            ? 'border-[#ffa100] bg-[#ffa100]/5 text-[#ffa100]'
                            : 'border-gray-200 text-gray-400 hover:border-gray-300'
                        }`}
                        disabled={status === 'loading'}
                      >
                        {t('guide_notification.modal.guide_zendesk')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setGuide('intercom')}
                        className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all duration-200 font-bold text-sm ${
                          guide === 'intercom'
                            ? 'border-[#ffa100] bg-[#ffa100]/5 text-[#ffa100]'
                            : 'border-gray-200 text-gray-400 hover:border-gray-300'
                        }`}
                        disabled={status === 'loading'}
                      >
                        {t('guide_notification.modal.guide_intercom')}
                      </button>
                    </div>
                  </div>

                  {status === 'error' && (
                    <div className="flex items-center gap-2 text-red-500 text-sm font-medium animate-fade-in">
                      <AlertCircle className="w-4 h-4" />
                      {t('guide_notification.modal.error_message')}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full bg-[#ffa100] hover:bg-[#ff9100] text-white font-bold py-5 rounded-2xl shadow-xl shadow-[#ffa100]/20 flex items-center justify-center gap-3 transition-all duration-200 transform hover:-translate-y-0.5 active:scale-[0.98] group disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {status === 'loading' ? (
                      <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Send className="w-5 h-5 group-hover:bounce transition-transform" />
                        <span className="text-lg">{t('guide_notification.modal.download_button')}</span>
                      </>
                    )}
                  </button>
                  <p className="text-center text-[11px] text-gray-400 font-medium">
                    {t('guide_notification.modal.subtitle')}
                  </p>
                 </form>
               )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes zoomIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
        .animate-zoom-in {
          animation: zoomIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .group:hover .group-hover\\:bounce {
          animation: bounce 1s infinite ease-in-out;
        }
      `}</style>
    </>
  );
};

export default GuideNotification;
